import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCompanyNotificationSettings,
  updateCompanyNotificationSettings,
} from '../../../../api/General/NotificationPreference';
import type { CompanyNotificationSettingView } from '../../../../api/General/NotificationPreference';
import NotificationPreferenceMatrix from '../../../../components/notifications/NotificationPreferenceMatrix';
import { Spinner } from '../../../../components/ui/Spinner';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';

const CompanyDefaultsTab: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<CompanyNotificationSettingView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompanyNotificationSettings();
      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
      } else {
        setError(result.errors?.[0]?.description || t('Failed to load settings'));
      }
    } catch (err) {
      setError(t('Failed to load settings'));
      console.error('Failed to load company notification settings:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSettingChange = useCallback(
    (notificationTypeId: number, field: string, value: boolean) => {
      setSettings((prev) =>
        prev.map((s) => {
          if (s.notificationTypeId !== notificationTypeId) return s;
          return { ...s, [field]: value };
        })
      );
      setHasChanges(true);
      setSaveSuccess(false);
    },
    []
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = settings.map((s) => ({
        notificationTypeId: s.notificationTypeId,
        emailsDisabled: s.emailsDisabled,
        mobilePushNotificationsDisabled: s.mobilePushNotificationsDisabled,
        inAppDisabled: s.inAppDisabled,
      }));
      const result = await updateCompanyNotificationSettings(payload);
      if (result.errors?.length) {
        setError(result.errors[0]?.description || t('Failed to save settings'));
        return;
      }
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save company notification settings:', err);
      setError(t('Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Description */}
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <AppIcon name="Info" size={18} className="text-primary mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p>
              {t('These settings control the default notification channels for all users in your company.')}
            </p>
            <p className="mt-1">
              {t('When a channel is disabled, users cannot enable it for that notification type.')}
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Matrix */}
      <NotificationPreferenceMatrix
        mode="admin"
        companySettings={settings}
        onCompanySettingChange={handleSettingChange}
      />

      {/* Save bar */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {saveSuccess && (
          <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
            <AppIcon name="Check" size={16} />
            {t('Settings saved successfully')}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSettings}
          disabled={saving}
        >
          {t('Reset')}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          loading={saving}
        >
          {t('Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default CompanyDefaultsTab;
